import { formatDistanceToNow } from 'date-fns'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Activity,
  CheckCircle,
  XCircle,
} from 'lucide-react'
import { type Site } from './shared'
import { useSiteLogs, useSiteActivity } from '../../hooks/useSites'

interface ActivityTabProps {
  site: Site
}

export function ActivityTab({ site }: ActivityTabProps) {
  const { data: logs, isLoading: logsLoading } = useSiteLogs(site.domain, 10)
  const { data: activity, isLoading: activityLoading } = useSiteActivity(site.domain, 20)

  if (logsLoading || activityLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-full" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Recent Logins */}
      <div>
        <h4 className="text-sm font-medium mb-2">Recent Logins</h4>
        {logs && logs.length > 0 ? (
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>User</TableHead>
                  <TableHead>Time</TableHead>
                  <TableHead className="text-right">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {logs.map((log) => (
                  <TableRow key={log.id}>
                    <TableCell className="font-mono text-xs">
                      {log.user_email}
                    </TableCell>
                    <TableCell className="text-muted-foreground text-xs">
                      {formatDistanceToNow(new Date(log.created_at), { addSuffix: true })}
                    </TableCell>
                    <TableCell className="text-right">
                      {log.success ? (
                        <CheckCircle className="h-4 w-4 text-green-500 inline" />
                      ) : (
                        <XCircle className="h-4 w-4 text-red-500 inline" />
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">No login history</p>
        )}
      </div>

      {/* Activity Timeline */}
      <div>
        <h4 className="text-sm font-medium mb-2">Activity Timeline</h4>
        {activity && activity.length > 0 ? (
          <div className="space-y-2">
            {activity.map((event) => (
              <div
                key={event.id}
                className="flex items-start gap-3 p-2 rounded-lg border text-sm"
              >
                <div className="text-muted-foreground mt-0.5">
                  <Activity className="h-4 w-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{event.event_type}</div>
                  <div className="text-muted-foreground text-xs">
                    {event.user_email || 'System'} &middot;{' '}
                    {formatDistanceToNow(new Date(event.created_at), { addSuffix: true })}
                  </div>
                </div>
                <Badge variant="outline" className="text-xs shrink-0">
                  {event.category}
                </Badge>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">No activity recorded</p>
        )}
      </div>
    </div>
  )
}
